import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import type { IEmployeeTalkTemplateComponent } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import styles from '../TemplateEditor/ETTTemplateEditor.module.css';
import type { ITemplateComponentProps } from '../TemplateEditor/types';

import { COMPONENT_TYPE_LABELS } from './ETTComponentFactory';

export interface IETTBaseTemplateComponentProps extends ITemplateComponentProps {
    renderEditContent?: () => React.ReactNode;
    renderViewContent?: () => React.ReactNode;
    hideLabels?: boolean;
}

export const ETTBaseTemplateComponent: React.FC<IETTBaseTemplateComponentProps> = ({
    component,
    isEditing,
    onEdit,
    onDelete,
    onMove,
    renderEditContent,
    renderViewContent,
    hideLabels,
}) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    const [selectedType, setSelectedType] = React.useState<IEmployeeTalkTemplateComponent | ''>('');
    const [labelValue, setLabelValue] = React.useState(component.label);
    const [descriptionValue, setDescriptionValue] = React.useState(component.description || '');

    const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLabelValue(newValue);
        if (onEdit && component) {
            onEdit({
                ...component,
                label: newValue,
                system: component.system,
            });
        }
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setDescriptionValue(newValue);
        if (onEdit && component) {
            onEdit({
                ...component,
                description: newValue || '',
                system: component.system,
            });
        }
    };

    const handleTypeChange = (event: SelectChangeEvent<IEmployeeTalkTemplateComponent | ''>) => {
        const type = event.target.value as IEmployeeTalkTemplateComponent;
        setSelectedType(type);
    };

    // Early return if no component
    if (!component) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Keine Komponente
                </Typography>
            </Box>
        );
    }

    // Component type selection
    if (!component.type && isEditing) {
        return (
            <Box className={styles.componentItem}>
                <FormControl fullWidth>
                    <InputLabel id="component-type-select-label">{langStrings.componentType}</InputLabel>
                    <Select
                        labelId="component-type-select-label"
                        value={selectedType}
                        onChange={handleTypeChange}
                        label={langStrings.componentType}
                    >
                        {Object.entries(COMPONENT_TYPE_LABELS).map(([type, label]) => (
                            <MenuItem key={type} value={type}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        );
    }

    return (
        <Box className={styles.componentItem}>
            {isEditing && (
                <Box className={styles.componentControls}>
                    {onMove && (
                        <>
                            <IconButton size="small" onClick={() => onMove('up')} title={langStrings.moveUp}>
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => onMove('down')} title={langStrings.moveDown}>
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </>
                    )}
                    {onDelete && (
                        <IconButton
                            size="small"
                            onClick={component.system ? undefined : () => onDelete?.(component.id)}
                            title={
                                component.system
                                    ? langStrings.cannotDeleteSystemComponent ||
                                      'System-Komponenten können nicht gelöscht werden'
                                    : langStrings.oftenUsed?.delete
                            }
                            disabled={component.system}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                    {component.system && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                            <Typography variant="caption" color="primary" fontWeight="bold">
                                {langStrings.systemComponent || 'System'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {isEditing ? (
                <>
                    <RowElement title={langStrings.componentName}>
                        <TextField
                            fullWidth
                            value={labelValue}
                            onChange={handleLabelChange}
                            size="small"
                            margin="dense"
                        />
                    </RowElement>
                    <RowElement title={langStrings.componentDescription}>
                        <TextField
                            fullWidth
                            value={descriptionValue}
                            onChange={handleDescriptionChange}
                            size="small"
                            margin="dense"
                            placeholder={langStrings.descriptionPlaceholder}
                            multiline
                            rows={2}
                        />
                    </RowElement>
                    {renderEditContent && renderEditContent()}
                </>
            ) : (
                <>
                    {!hideLabels && (
                        <>
                            {component.label && (
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {component.label}
                                    {component.required && <span style={{ color: 'red' }}> *</span>}
                                </Typography>
                            )}
                            {component.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {component.description}
                                </Typography>
                            )}
                        </>
                    )}
                    {renderViewContent && renderViewContent()}
                </>
            )}
        </Box>
    );
};

export default ETTBaseTemplateComponent;
